import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "@/hooks/useTheme";

interface NavbarProps {
  onQuoteClick: () => void;
}

export default function Navbar({ onQuoteClick }: NavbarProps) {
  const { theme, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const isHome = location.pathname === "/";
  const heroMode = !scrolled && theme === "light" && isHome;
  const useWhiteLogo = heroMode || theme === "dark";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/ahis", label: "AHIS" },
    { to: "/contact", label: "Contact" },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen
          ? "bg-white/90 dark:bg-dark-bg/90 backdrop-blur-md border-b border-gray-200/50 dark:border-dark-border/50 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <nav className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <img
              src={
                useWhiteLogo
                  ? "https://storage.readdy-site.link/project_files/47b2e0f1-f782-46fd-9795-faa524ec7a75/6f680f81-acb5-43c1-987a-0f33b54ce907_Presuni-logo-icon-1.png?v=b7ba9735e9e07e665ff0b08f3d253af5"
                  : "https://static.readdy.ai/image/cd6790f2d50eb1ecfa87fc97aafdc7d9/60656d058e98f3c366b059b99267fdcc.png"
              }
              alt="Presuni"
              className="h-8 md:h-10 w-auto"
            />
            <span
              className={`text-xl md:text-2xl font-bold tracking-tight ${
                heroMode && !mobileOpen
                  ? "text-white"
                  : "text-gray-900 dark:text-white"
              }`}
            >
              PRESUNI<span className="text-brand-500">.</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-medium transition-colors duration-200 ${
                  heroMode && !mobileOpen
                    ? isActive(link.to)
                      ? "text-brand-500"
                      : "text-white/80 hover:text-white"
                    : isActive(link.to)
                      ? "text-brand-500"
                      : "text-gray-700 dark:text-gray-300 hover:text-brand-500"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                heroMode
                  ? "text-white/80 hover:bg-white/10"
                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-card"
              }`}
              aria-label="Toggle theme"
            >
              {theme === "light" ? (
                <i className="ri-moon-line text-lg" />
              ) : (
                <i className="ri-sun-line text-lg" />
              )}
            </button>
            <button
              onClick={onQuoteClick}
              className="px-5 py-2.5 rounded-full bg-brand-500 text-white text-sm font-semibold hover:bg-brand-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Get a Quote
            </button>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <button
              onClick={toggleTheme}
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                heroMode && !mobileOpen
                  ? "text-white/80 hover:bg-white/10"
                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-card"
              }`}
              aria-label="Toggle theme"
            >
              {theme === "light" ? (
                <i className="ri-moon-line text-lg" />
              ) : (
                <i className="ri-sun-line text-lg" />
              )}
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                heroMode && !mobileOpen
                  ? "text-white/90 hover:bg-white/10"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-card"
              }`}
              aria-label="Toggle menu"
            >
              <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-xl`} />
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-4 border-t border-gray-100 dark:border-dark-border/50 pt-4">
            <div className="flex flex-col gap-2 px-2">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.to)
                      ? "bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-card"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <button
                onClick={() => {
                  setMobileOpen(false);
                  onQuoteClick();
                }}
                className="mt-2 px-5 py-2.5 rounded-full bg-brand-500 text-white text-sm font-semibold hover:bg-brand-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get a Quote
              </button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}